import React from 'react';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import useMediaQuery from '@mui/material/useMediaQuery';
import { Theme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { JobDiscoveryFilterContainerProps } from 'types/connectTypes';
import { JobDiscoveryLocationMapFilter } from './JobDiscoveryLocationMapFilter';
import { Filters } from './JobDiscoveryFilters';
import { ConnectFilterName } from 'features/Connect/utils/connectEnums';
import { useConnectContext } from '../../Context/ConnectContextProvider';
import { MobileDrawer } from 'sharedComponents/Drawer/MobileDrawer';
import { MobileDrawerControlButtonGroup } from 'sharedComponents/Drawer/MobileDrawerControlButtonGroup';
import { MobileDrawerTitles } from 'sharedComponents/Filters/constants';

export const JobDiscoveryFilterContainer: React.FC<JobDiscoveryFilterContainerProps> = ({
  filterValues,
  jobDiscoveryActions,
  filterOptions,
  activeFilters,
}) => {
  const { connectContextActions } = useConnectContext();

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.between('xs', 'lg'));

  const [drawerOpen, setDrawerOpen] = React.useState(false);

  const handleDrawerClose = () => setDrawerOpen(false);

  const handleDrawerOpen = () => setDrawerOpen(true);

  const handleDrawerSubmit = () => {
    setDrawerOpen(false);
  };

  const getSelectedOptions = (selectedOptions) => {
    if (!Array.isArray(selectedOptions)) {
      return [];
    }
    return selectedOptions.map((option) => option.value);
  };

  const updateSelectedFilterOptions = (
    selectedValues,
    options,
    updateSelectedValues,
    setFilterIsActive,
    filtername
  ) => {
    // ignore events until options is loaded
    if (filterOptions.isLoadingFilterOptions) {
      return;
    }
    connectContextActions.setIsLoading(true);
    jobDiscoveryActions.setIsLoadingInitialFilterResults(true);
    const selectedOptions = getSelectedOptions(selectedValues);

    if (selectedValues?.length === options?.length || selectedValues?.length === 0) {
      updateSelectedValues([]);
      setFilterIsActive(filtername, false);
    } else {
      updateSelectedValues(selectedOptions);
      setFilterIsActive(filtername, true);
    }
    connectContextActions.setIsLoading(false);
    jobDiscoveryActions.setIsLoadingInitialFilterResults(false);
  };

  const setSelectedSubjectOptions = (selectedSubjectOptions) => {
    updateSelectedFilterOptions(
      selectedSubjectOptions,
      filterOptions.subjectOptions,
      jobDiscoveryActions.setSelectedSubjectIds,
      jobDiscoveryActions.setFilterIsActive,
      ConnectFilterName.SUBCATEGORIES
    );
  };

  const setSelectedGradeOptions = (selectedGradeOptions) => {
    updateSelectedFilterOptions(
      selectedGradeOptions,
      filterOptions.gradeOptions,
      jobDiscoveryActions.setSelectedGrades,
      jobDiscoveryActions.setFilterIsActive,
      ConnectFilterName.GRADES
    );
  };

  const setSelectedSchoolOptions = (selectedSchoolOptions) => {
    updateSelectedFilterOptions(
      selectedSchoolOptions,
      filterOptions.schoolOptions,
      jobDiscoveryActions.setSelectedSchoolIds,
      jobDiscoveryActions.setFilterIsActive,
      ConnectFilterName.SCHOOLS
    );
  };

  const setSelectedDistrictOptions = (selectedDistrictOptions) => {
    updateSelectedFilterOptions(
      selectedDistrictOptions,
      filterOptions.districtOptions,
      jobDiscoveryActions.setSelectedDistrictIds,
      jobDiscoveryActions.setFilterIsActive,
      ConnectFilterName.DISTRICTS
    );
  };

  // Calculate the number of selected filter options for each filter
  const totalFilterOptionsSelected = (val) => (Array.isArray(val) ? val.length : val ? 1 : 0);
  const activeFilterCount =
    totalFilterOptionsSelected(filterValues.grades) +
    totalFilterOptionsSelected(filterValues.subcategories) +
    totalFilterOptionsSelected(filterValues.district_nces_ids) +
    totalFilterOptionsSelected(filterValues.school_nces_ids);

  return (
    <FilterContainer>
      <Grid container spacing={1.5} justifyContent={'space-between'}>
        <Grid item xs={12}>
          <JobDiscoveryLocationMapFilter jobDiscoveryActions={jobDiscoveryActions} />
        </Grid>
        <Grid item xs={12}>
          {isMobile ? (
            <>
              <MobileDrawerControlButtonGroup
                buttonTitles={[MobileDrawerTitles.FILTERS]}
                onClickHandlers={[handleDrawerOpen]}
                activeDrawers={[Object.values(activeFilters).some(Boolean)]}
                showActiveCount={true}
                activeCount={activeFilterCount}
              />
              <MobileDrawer
                isOpen={drawerOpen}
                closeHandler={handleDrawerClose}
                openHandler={handleDrawerOpen}
                anchor="bottom"
                title="Filters"
                buttonText="Apply Filters"
                onSubmitHandler={handleDrawerSubmit}
                showCloseButton={true}
                dataTestId="job-discovery-filters-mobile-drawer"
                isLoading={false}
                isDisabled={false}
              >
                <Filters
                  filterValues={filterValues}
                  filterOptions={filterOptions}
                  isMobile={isMobile}
                  setSelectedGradeOptions={setSelectedGradeOptions}
                  setSelectedSubjectOptions={setSelectedSubjectOptions}
                  setSelectedDistrictOptions={setSelectedDistrictOptions}
                  setSelectedSchoolOptions={setSelectedSchoolOptions}
                  activeFilters={activeFilters}
                />
              </MobileDrawer>
            </>
          ) : (
            <Filters
              filterValues={filterValues}
              filterOptions={filterOptions}
              isMobile={isMobile}
              setSelectedGradeOptions={setSelectedGradeOptions}
              setSelectedSubjectOptions={setSelectedSubjectOptions}
              setSelectedDistrictOptions={setSelectedDistrictOptions}
              setSelectedSchoolOptions={setSelectedSchoolOptions}
              activeFilters={activeFilters}
            />
          )}
        </Grid>
      </Grid>
    </FilterContainer>
  );
};

const FilterContainer = styled(Box)(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    margin: `0px 0px ${theme.spacing(1)}`,
  },
}));

export const InputStyles = (theme: Theme) => ({
  width: '100%',
  fontSize: theme.typography.subtitle.fontSize,
  '& .MuiOutlinedInput-root': {
    borderRadius: theme.shape.borderRadius * 0.5,
  },
  '& .MuiInputLabel-root': {
    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.body1.fontSize,
    },
  },
  '& .MuiInputBase-input': {
    [theme.breakpoints.down('md')]: {
      fontSize: theme.typography.subtitle.fontSize,
    },
  },
});
